import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  CRM_DROPDOWN_MAX_OPTIONS,
  CRM_MAX_FIELD_LENGTH,
} from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { FileUpload } from '../../../components/FileUpload/FileUpload';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InfoText } from '../../../components/InfoText';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import {
  DropdownOption,
  DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { AttachedFile, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { useIngredientsState } from '../IngredientsState';

export const StepIngredients: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const [
    { ingredientsFiles, rawMaterial, azelisRawMaterials },
    { setIngredientsFiles, setRawMaterial, setAzelisRawMaterials },
  ] = useBriefingState();

  const [{ countryMarketConfig }] = useCountryMarketState();

  const [ingredientsState] = useIngredientsState();

  const [isRawMaterialValid, setIsRawMaterialValid] = useState(true);
  const isStepValid = useMemo(() => isRawMaterialValid, [isRawMaterialValid]);
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const dropDownIngredients: DropdownOption[] =
    useMemo((): DropdownOption[] => {
      if (!countryMarketConfig) return [];
      const countryMarket = countryMarketConfig.customerPortalMarketSegmentId;

      return (
        ingredientsState[countryMarket]?.ingredients?.map(
          (ingredient): DropdownOption => ({
            value: ingredient.itemNumber,
            name: ingredient.name,
            subtitle: ingredient.reference,
          }),
        ) || []
      );
    }, [ingredientsState, countryMarketConfig]);

  const dispatch = useDispatch();

  const onRawMaterialChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setRawMaterial(event.target.value));
  };

  const onAzelisRawMaterialChange = (
    selectedOption: DropdownOptionsWithUndefined,
  ) => {
    if (!Array.isArray(selectedOption)) return;

    dispatch(setAzelisRawMaterials(selectedOption));
  };

  const onFilesChange = useCallback(
    (files: AttachedFile[]) => {
      dispatch(setIngredientsFiles(files));
    },
    [dispatch, setIngredientsFiles],
  );

  const [initialRawMaterial] = useState(rawMaterial);
  const [initialFiles] = useState(ingredientsFiles);

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        text={t(`topics:ingredients:heading:general`)}
        margin={0}
        optionalField={mandatoryFields['ingredients']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <div className="w-full md:w-8/12">
          <TemplateFlexGridContainer.BriefingStepSection>
            <p>{t('topics:ingredients:upload')}</p>
            {/* Container div to stop iPad button height issue */}
            <div>
              <InputGroup>
                <FileUpload
                  label={t('labels:addAttachment')}
                  {...(initialFiles ? { initialFiles: initialFiles } : {})}
                  onChange={onFilesChange}
                />
              </InputGroup>
            </div>
            <div>
              <InputGroup label={t('topics:ingredients:azelisRawMaterials')}>
                <Dropdown
                  options={dropDownIngredients}
                  initialSelection={azelisRawMaterials}
                  typeahead={true}
                  typeaheadMinChars={1}
                  multiselect={true}
                  maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                  onChange={onAzelisRawMaterialChange}
                />
              </InputGroup>
            </div>
            <div>
              <InputGroup label={t('topics:ingredients:other')}>
                <Input
                  {...(initialRawMaterial
                    ? { initialValue: initialRawMaterial }
                    : {})}
                  onChange={onRawMaterialChange}
                  maxLength={CRM_MAX_FIELD_LENGTH}
                  onValidate={setIsRawMaterialValid}
                />
              </InputGroup>
            </div>
            <div>
              <InfoText
                color="darker"
                variant="small"
                text={t('components:fileUpload:privacy')}
              />
            </div>
          </TemplateFlexGridContainer.BriefingStepSection>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
